import React, { useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import { useSelector } from "react-redux";
import NunitoText from "../general/NunitoText";
import ReusableAutocompleteWithID from "../general/ReusableAutocompleteWithID";
import ReusableButton from "../general/ReusableButton";
import ReusableCheckbox from "../general/ReusableCheckbox";
import { useSnackbar } from "notistack";
import { showWarningSwal } from "../../functions/alert";
import { showNotification } from "../../functions/snackbar";
import {
  extractAssignmentFilterOptions,
  toAssignmentSelectOption,
} from "../../functions/assignmentFilterOptions";
import { getContestPaperExportFilterOptions } from "../../functions/getData";
import { exportContestPaper } from "../../functions/postData";
import { isAdmin } from "../../functions/checkrole";

const rowStyle = { marginBottom: 24 };
const formBoxStyle = {
  backgroundColor: "#FFF",
  borderRadius: 12,
  boxShadow: "0 0 3px #9E9E9E",
  padding: 24,
  marginBottom: 24,
};
const sectionLabelStyle = {
  color: "#000",
  fontSize: 16,
  fontFamily: "Nunito",
  fontWeight: 600,
  fontStyle: "italic",
  marginBottom: 4,
};

// `get_filter` values for translations/exports/contest-paper/filter-options
const FILTER = {
  competition: "competition",
  country: "country",
  languages: "languages",
  grade: "grade",
  exportType: "export_type",
  options: "options",
};

/** API row (object or bare string) → `{ id, option }` for ReusableAutocompleteWithID. */
const toSelectOption = (item) =>
  item !== null && typeof item === "object"
    ? toAssignmentSelectOption(item)
    : { id: item, option: String(item) };

const toSelectOptions = (res, keys) =>
  extractAssignmentFilterOptions(res, keys)
    .map(toSelectOption)
    .filter(
      (o) => o.id != null && o.id !== "" && String(o.option).trim() !== "",
    );

export default function NewExportForm({ setLoading, onCreated }) {
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const user = useSelector((state) => state.user);

  const [competitionOptions, setCompetitionOptions] = useState([]);
  const [countryOptions, setCountryOptions] = useState([]);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [gradeFilterOptions, setGradeFilterOptions] = useState([]);
  const [exportTypeOptions, setExportTypeOptions] = useState([]);
  /** `[{ value, label, default }]` exactly as returned by `get_filter=options`. */
  const [exportOptions, setExportOptions] = useState([]);

  const [competition, setCompetition] = useState("");
  const [countryId, setCountryId] = useState(
    isAdmin() ? "" : user?.country_id || "",
  );
  const [languages, setLanguages] = useState([]);
  const [grade, setGrade] = useState("");
  const [exportType, setExportType] = useState("");
  /** `{ [option.value]: boolean }` — every retrieved option is sent in the export payload. */
  const [optionValues, setOptionValues] = useState({});

  const effectiveCountryId = useMemo(() => {
    if (isAdmin()) return countryId;
    return user?.country_id || "";
  }, [countryId, user?.country_id]);

  // —— Filters that don't depend on other selections. ——
  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    getContestPaperExportFilterOptions(
      `?get_filter=${FILTER.competition}`,
      signal,
    )
      .then((res) =>
        setCompetitionOptions(
          toSelectOptions(res, ["competition", "competitions"]),
        ),
      )
      .catch(() => setCompetitionOptions([]));

    getContestPaperExportFilterOptions(
      `?get_filter=${FILTER.exportType}`,
      signal,
    )
      .then((res) => {
        const raw = extractAssignmentFilterOptions(res, [
          "export_type",
          "export_types",
        ]);
        const list = toSelectOptions(res, ["export_type", "export_types"]);
        setExportTypeOptions(list);
        const preset = raw.find((o) => o && typeof o === "object" && o.default);
        if (preset) setExportType(toSelectOption(preset).id);
        else if (list.length === 1) setExportType(list[0].id);
      })
      .catch(() => setExportTypeOptions([]));

    getContestPaperExportFilterOptions(`?get_filter=${FILTER.options}`, signal)
      .then((res) => {
        const list = extractAssignmentFilterOptions(res, ["options"]).filter(
          (o) => o && typeof o === "object" && o.value,
        );
        setExportOptions(list);
        setOptionValues(
          Object.fromEntries(list.map((o) => [o.value, Boolean(o.default)])),
        );
      })
      .catch(() => {
        setExportOptions([]);
        setOptionValues({});
      });

    return () => controller.abort();
  }, []);

  // —— Grade (depends on competition) ——
  useEffect(() => {
    setGrade("");
    setGradeFilterOptions([]);
    if (!competition) return;
    getContestPaperExportFilterOptions(
      `?get_filter=${FILTER.grade}&competition=${competition}`,
    )
      .then((res) =>
        setGradeFilterOptions(toSelectOptions(res, ["grade", "grades"])),
      )
      .catch(() => setGradeFilterOptions([]));
  }, [competition]);

  // —— Country (admin only, optional; non-admins use their own country). ——
  useEffect(() => {
    if (!isAdmin()) return;
    setCountryId("");
    setCountryOptions([]);
    if (!competition || !grade) return;
    getContestPaperExportFilterOptions(
      `?get_filter=${FILTER.country}&competition=${competition}&grade=${grade}`,
    )
      .then((res) =>
        setCountryOptions(toSelectOptions(res, ["country", "countries"])),
      )
      .catch(() => setCountryOptions([]));
  }, [competition, grade]);

  // —— Languages (country narrows the list only when there is one). ——
  useEffect(() => {
    setLanguages([]);
    setLanguageOptions([]);
    if (!competition || !grade) return;
    const countryQuery = effectiveCountryId
      ? `&country=${effectiveCountryId}`
      : "";
    getContestPaperExportFilterOptions(
      `?get_filter=${FILTER.languages}&competition=${competition}&grade=${grade}${countryQuery}`,
    )
      .then((res) =>
        setLanguageOptions(toSelectOptions(res, ["languages", "language"])),
      )
      .catch(() => setLanguageOptions([]));
  }, [competition, grade, effectiveCountryId]);

  const setOptionValue = (key, value) =>
    setOptionValues((prev) => ({ ...prev, [key]: value }));

  const onSubmit = () => {
    let warning = "";
    if (!competition) warning += "Please select a competition.<br>";
    if (!grade) warning += "Please select a grade.<br>";
    if (!languages.length)
      warning += "Please select at least one language.<br>";
    // Country is optional for admins (sent as null when not selected).
    if (!isAdmin() && !effectiveCountryId)
      warning += "Missing country context for this export.<br>";
    if (!exportType) warning += "Please select an export type.<br>";
    if (warning) return showWarningSwal(warning);

    const payload = {
      ...optionValues,
      competition,
      grade,
      languages: languages.map(String),
      country: effectiveCountryId || null,
      export_type: exportType,
    };

    setLoading(true);
    exportContestPaper(payload)
      .then((d) => {
        setLoading(false);
        if (d?.status && ![200, 201, 202].includes(Number(d.status))) return;
        showNotification(
          "success",
          d?.message ||
            (d?.fileName ? `Downloaded ${d.fileName}` : "Export started"),
          enqueueSnackbar,
          closeSnackbar,
        );
        onCreated && onCreated(d);
      })
      .catch(() => setLoading(false));
  };

  const gradeReady = Boolean(competition && grade);

  return (
    <Box style={formBoxStyle}>
      <NunitoText
        value="New Export"
        fontSize={24}
        fontWeight={700}
        color="#5E75C3"
        marginBottom={16}
      />

      <Grid container justifyContent="space-between" style={rowStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Competition*"
          state={competition}
          setState={setCompetition}
          required
          options={competitionOptions}
        />
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Grade*"
          state={grade}
          setState={setGrade}
          required
          options={gradeFilterOptions}
          grayedOut={!competition}
          readOnly={!competition}
        />
      </Grid>

      <Grid container justifyContent="space-between" style={rowStyle}>
        {isAdmin() && (
          <ReusableAutocompleteWithID
            type="withLabel"
            width={500}
            placeholder="Country"
            state={countryId}
            setState={setCountryId}
            options={countryOptions}
            grayedOut={!gradeReady}
            readOnly={!gradeReady}
          />
        )}
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Languages*"
          required
          state={languages}
          setState={setLanguages}
          multiple
          options={languageOptions}
          grayedOut={!gradeReady}
          readOnly={!gradeReady}
        />
        {!isAdmin() && <div style={{ width: 500 }} />}
      </Grid>

      <Grid container justifyContent="space-between" style={rowStyle}>
        <ReusableAutocompleteWithID
          type="withLabel"
          width={500}
          placeholder="Export Type*"
          state={exportType}
          setState={setExportType}
          required
          options={exportTypeOptions}
        />
        <div style={{ width: 500 }} />
      </Grid>

      {exportOptions.length > 0 && (
        <Grid container direction="column" style={rowStyle}>
          <span style={sectionLabelStyle}>Options</span>
          <Grid container>
            {exportOptions.map((o) => (
              <ReusableCheckbox
                key={o.value}
                type="modal"
                label={o.label || o.value}
                fontSize={16}
                marginRight={32}
                state={Boolean(optionValues[o.value])}
                setState={setOptionValue}
                target={o.value}
              />
            ))}
          </Grid>
        </Grid>
      )}

      <Grid container justifyContent="flex-end" style={{ marginTop: 16 }}>
        <ReusableButton
          text="Start Export"
          bgColor="#5E75C3"
          fontSize={18}
          height={50}
          width={220}
          onClick={onSubmit}
        />
      </Grid>
    </Box>
  );
}
